import { useCallback, useEffect, useState } from "react";
import type {
  ComposeJobResponse,
  ComposeStatus,
  EditResponse,
  EditRevisionResponse,
  EditSettings,
  FinalInfo,
  GenerationErrorCode,
  GenerationJobState,
  MeResponse,
  Motion,
  VideoJobResponse,
  WorkResponse,
} from "@studio/shared";
import { watchJob } from "./job-poll";
import { loadSession, readSessionLoss, workIdFromApiPath } from "./session";
import { publishPendingGifts } from "./gifts";

const apiBase = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * 학생 참여가 풀렸다 — 토큰이 사라졌거나(로컬 저장분 소멸) 서버가 더는 받아주지 않는다(401).
 * 「수업이 끝났어요」(`mvs:session-ended`, 410)와 **다른 일**이다.
 * 수업은 살아 있고 학생만 튕긴 것이므로, 안내도 되돌릴 곳도 달라야 한다(코드 다시 넣기).
 */
export const sessionLostEvent = "mvs:session-lost";

/** 참여가 풀렸을 때 학생에게 보일 말. 오류 객체가 아니라 사람 말이어야 한다. */
export const SESSION_LOST_MESSAGE = "수업 참여가 풀렸어요. 수업 코드를 다시 넣어 주세요.";

function announceSessionLost(): void {
  window.dispatchEvent(new Event(sessionLostEvent));
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit & { authenticated?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  if (init.authenticated !== false) {
    // 🔑 작품 경로면 **그 작품의** 토큰, 아니면 이 탭이 보고 있는 작품의 토큰(session.ts).
    //    한 칸을 같이 쓰던 v1 에서는 다른 탭의 수업 토큰이 실려 403 이 났다(2026-10-12).
    const token = loadSession(workIdFromApiPath(path) ?? undefined)?.token;
    // 🚨 토큰이 없으면 **보내지 않는다.** 종전에는 헤더 없이 그대로 보내서
    //    서버가 「인증이 필요합니다」 401 을 돌려줬고, 401 에는 아무 처리가 없어
    //    학생은 스토리보드에 갇힌 채 오류만 봤다(2026-08-12 prod 실측 B3).
    if (!token) {
      // 🚨 «토큰이 없다» 는 결과이지 원인이 아니다. 원인은 세션을 지운 쪽이 알고 있고,
      //    그걸 브레드크럼으로 남겨 뒀다(session.ts). 여기서 함께 찍어야 개발자도구 한 화면에
      //    「어느 요청이 · 왜」 가 같이 남는다 — B3 에서 없던 것이 정확히 이 한 줄이다.
      const crumb = readSessionLoss();
      console.error(
        `[mvs:auth-required] ${init.method ?? "GET"} ${path} — 토큰 없음 · 직전 손실=${
          crumb ? `${crumb.reason}(${new Date(crumb.at).toISOString()})${crumb.detail ? ` · ${crumb.detail}` : ""}` : "기록 없음"
        }`,
      );
      announceSessionLost();
      throw new ApiError(401, "AUTH_REQUIRED", SESSION_LOST_MESSAGE);
    }
    headers.set("authorization", `Bearer ${token}`);
  }
  const response = await fetch(`${apiBase}${path}`, { ...init, headers });
  const data = (await response.json().catch(() => null)) as
    | { error?: { code?: string; message?: string } }
    | T
    | null;
  if (!response.ok) {
    const error = data && typeof data === "object" && "error" in data ? data.error : undefined;
    const apiError = new ApiError(
      response.status,
      error?.code ?? "INTERNAL",
      error?.message ?? "요청을 처리하지 못했습니다.",
    );
    if (response.status === 410 && !isEndedReadException(path, init.method)) {
      window.dispatchEvent(new Event("mvs:session-ended"));
    }
    // 토큰을 붙여 보냈는데도 401 이면 서버가 더는 받아주지 않는 것이다(만료·서명 실패).
    // 종전에는 여기에 아무 처리가 없어 되돌리지도 안내하지도 못했다.
    if (response.status === 401 && init.authenticated !== false) {
      announceSessionLost();
      throw new ApiError(response.status, apiError.code, SESSION_LOST_MESSAGE);
    }
    throw apiError;
  }
  return data as T;
}

export function getMe(): Promise<MeResponse> {
  return apiRequest<MeResponse>("/api/me?gifts_v=2").then((response) => {
    publishPendingGifts(response.pending_gifts);
    return response;
  });
}

function workPath(workId: string, suffix: string): string {
  return `/api/works/${encodeURIComponent(workId)}${suffix}`;
}

export function getEdit(workId: string): Promise<EditResponse> {
  return apiRequest<EditResponse>(workPath(workId, "/edit"));
}

export function saveEditSettings(
  workId: string,
  settings: EditSettings,
): Promise<EditRevisionResponse> {
  return apiRequest<EditRevisionResponse>(workPath(workId, "/edit-settings"), {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

/** 뮤직비디오 제목(027). 빈 값이면 지운다. 리비전이 오른다. */
export function saveWorkTitle(
  workId: string,
  title: string,
): Promise<EditRevisionResponse & { custom_title: string | null; final_outdated?: boolean }> {
  return apiRequest(workPath(workId, "/title"), {
    method: "PUT",
    body: JSON.stringify({ title }),
  });
}

/** 노래 제목. 다른 수업으로 이미 가져간 노래의 사본 제목은 바뀌지 않는다. */
export function renameTrack(workId: string, trackId: string, title: string): Promise<{ title: string }> {
  return apiRequest(workPath(workId, `/tracks/${encodeURIComponent(trackId)}`), {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
}

export function saveCutOrder(workId: string, order: string[]): Promise<EditRevisionResponse> {
  return apiRequest<EditRevisionResponse>(workPath(workId, "/cuts/order"), {
    method: "PUT",
    body: JSON.stringify({ order }),
  });
}

export function saveCutMotion(
  workId: string,
  cutId: string,
  motion: Motion,
): Promise<EditRevisionResponse> {
  return apiRequest<EditRevisionResponse>(
    workPath(workId, `/cuts/${encodeURIComponent(cutId)}/motion`),
    { method: "PUT", body: JSON.stringify({ motion }) },
  );
}

export function startVideoJob(
  workId: string,
  cutId: string,
  idempotencyKey: string,
): Promise<VideoJobResponse> {
  return apiRequest<VideoJobResponse>(
    workPath(workId, `/cuts/${encodeURIComponent(cutId)}/video-job`),
    { method: "POST", headers: { "Idempotency-Key": idempotencyKey } },
  );
}

export function startCompose(
  workId: string,
  idempotencyKey: string,
): Promise<ComposeJobResponse> {
  return apiRequest<ComposeJobResponse>(workPath(workId, "/compose"), {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
  });
}

export function stopCompose(
  workId: string,
  jobId?: string | null,
): Promise<{ state: GenerationJobState | "idle"; cancelled?: boolean }> {
  // 🩸 job_id 없이 보내면 서버가 «그때의 최신 합성» 을 취소한다 — 늦게 도착한 두 번째 중지가
  //    새로 시작한 합성을 끊을 수 있었다(Codex 검수 2026-10-21). 이 작업일 때만 취소하게 붙인다.
  const query = jobId ? `?job_id=${encodeURIComponent(jobId)}` : "";
  return apiRequest(workPath(workId, `/compose${query}`), { method: "DELETE" });
}

export function getComposeStatus(workId: string): Promise<ComposeStatus> {
  return apiRequest<ComposeStatus>(workPath(workId, "/compose/status"));
}

export function getFinal(workId: string): Promise<FinalInfo> {
  return apiRequest<FinalInfo>(workPath(workId, "/final"));
}

export async function downloadFinalVideo(workId: string, title: string): Promise<void> {
  const headers = new Headers();
  const token = loadSession(workId)?.token;
  if (token) headers.set("authorization", `Bearer ${token}`);
  const response = await fetch(`${apiBase}${workPath(workId, "/final/download")}`, { headers });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as
      | { error?: { code?: string; message?: string } }
      | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? "INTERNAL",
      body?.error?.message ?? "영상을 저장하지 못했어요.",
    );
  }
  const { url } = await response.json() as { url: string };
  // R2 서명 URL 을 fetch 로 받으면 CORS 에 막힌다 — 앵커 내비게이션이면 CORS 비적용이고,
  // 파일명은 서명 URL 의 Content-Disposition(attachment) 이 정한다.
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${title.replace(/[\\/:*?"<>|]/gu, " ").trim() || "뮤직비디오"}.mp4`;
  anchor.click();
}

function isEndedReadException(path: string, method = "GET"): boolean {
  const verb = method.toUpperCase();
  if (verb === "GET" && path.split("?")[0] === "/api/me") return true;
  if (verb === "GET" && /^\/api\/jobs\/[^/]+\/progress$/u.test(path)) return true;
  if (verb === "GET" && /^\/api\/works\/[^/]+$/u.test(path)) return true;
  if (verb === "GET" && /^\/api\/works\/[^/]+\/song-codes?$/u.test(path)) return true;
  return verb === "POST" && /^\/api\/works\/[^/]+\/finish$/u.test(path);
}

export interface WorkQuery {
  data: WorkResponse | null;
  loading: boolean;
  error: ApiError | null;
  refresh: () => Promise<WorkResponse | null>;
}

export const remainingSlotsEvent = "mvs:remaining-slots";

export function useWork(workId: string): WorkQuery {
  const [data, setData] = useState<WorkResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const refresh = useCallback(async () => {
    try {
      const next = await apiRequest<WorkResponse>(`/api/works/${encodeURIComponent(workId)}`);
      setData(next);
      setError(null);
      return next;
    } catch (caught) {
      const nextError = caught instanceof ApiError
        ? caught
        : new ApiError(500, "INTERNAL", "작품을 불러오지 못했습니다.");
      setError(nextError);
      return null;
    } finally {
      setLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const updateRemaining = (event: Event): void => {
      const slots = (event as CustomEvent<number>).detail;
      if (!Number.isFinite(slots)) return;
      setData((current) => current ? { ...current, remaining_slots: Math.max(0, slots) } : current);
    };
    window.addEventListener(remainingSlotsEvent, updateRemaining);
    return () => window.removeEventListener(remainingSlotsEvent, updateRemaining);
  }, []);

  return { data, loading, error, refresh };
}

export interface ProgressResponse {
  state: GenerationJobState;
  error_code?: GenerationErrorCode;
  track_id?: string;
}

export interface JobProgressQuery {
  progress: ProgressResponse | null;
  stopped: boolean;
  error: ApiError | null;
}

const terminalStates = new Set<GenerationJobState>([
  "succeeded",
  "failed",
  "cancelled",
  "outcome_unknown",
  "superseded",
]);

/** 폴링 줄기가 «무엇을 가져오고 무엇이 끝인가» 를 아는 유일한 자리. jobId 하나당 한 벌만 돈다. */
const jobPollDriver = {
  fetchProgress: (jobId: string) =>
    apiRequest<ProgressResponse>(`/api/jobs/${encodeURIComponent(jobId)}/progress`),
  isTerminal: (progress: ProgressResponse) => terminalStates.has(progress.state),
};

function toApiError(caught: unknown): ApiError {
  return caught instanceof ApiError
    ? caught
    : new ApiError(0, "NETWORK", "서버에 연결하지 못했습니다.");
}

/**
 * 잡 하나가 **끝날 때까지** 기다린다(훅이 아니라 그냥 함수 — 순차 처리에서 쓴다).
 *
 * 🔑 `useJobProgress` 와 **같은 폴링 줄기**를 탄다(`watchJob`). 종전엔 각자 폴링을 돌려서,
 *    배치 생성 중 같은 jobId 를 둘이 동시에 치고 있었다 — 서버에 묻는 횟수가 두 배였다.
 */
export async function waitForJob(
  jobId: string,
  options: { signal?: AbortSignal } = {},
): Promise<ProgressResponse | null> {
  if (options.signal?.aborted) return null;
  return new Promise<ProgressResponse | null>((resolve) => {
    let unsubscribe: (() => void) | null = null;
    let settled = false;
    const finish = (value: ProgressResponse | null): void => {
      if (settled) return;
      settled = true;
      unsubscribe?.();
      options.signal?.removeEventListener("abort", onAbort);
      resolve(value);
    };
    function onAbort(): void {
      finish(null);
    }
    options.signal?.addEventListener("abort", onAbort);
    unsubscribe = watchJob(jobId, jobPollDriver, (update) => {
      if (!update.stopped) return;
      // 🚨 «멈췄다» 는 두 가지다 — 잡이 끝났거나, 연속 실패로 포기했거나.
      //    후자는 결과가 없으므로 null 을 준다(종전 구현과 같다).
      const progress = update.progress;
      finish(progress && terminalStates.has(progress.state) ? progress : null);
    });
    // 이미 끝난 잡이라 통지가 «동기» 로 왔으면 위에서 unsubscribe 가 아직 null 이었다 — 여기서 끊는다.
    if (settled) unsubscribe();
  });
}

export function useJobProgress(jobId: string | null): JobProgressQuery {
  const [query, setQuery] = useState<JobProgressQuery>({
    progress: null,
    stopped: !jobId,
    error: null,
  });

  useEffect(() => {
    if (!jobId) {
      setQuery({ progress: null, stopped: true, error: null });
      return undefined;
    }
    setQuery({ progress: null, stopped: false, error: null });
    return watchJob(jobId, jobPollDriver, (update) => {
      setQuery({
        progress: update.progress,
        stopped: update.stopped,
        error: update.error === null ? null : toApiError(update.error),
      });
    });
  }, [jobId]);

  return query;
}
